import { useRef, useState } from "react";
import "./registerconfirm.scss";
import { BrandButton } from "../components/form/buttons";
import { ApiEndpoint } from "../components/utils";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAt,
  faCommentSms,
  faSpinner,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { Dialog, DialogContent, Checkbox } from "@mui/material";
import { BrandButtonWhite } from "../components/form/buttons";

interface RegistrationData {
  userid: number;
  token: string;
  email: string;
  phone: string;
}

const RegisterConfirm = () => {
  const navigate = useNavigate();
  const [sendingEmail, setSendingEmail] = useState(false);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [verified, setVerified] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);

  const registrationData: RegistrationData | null = (() => {
    try {
      const stored = localStorage.getItem("registrationData");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/sendVerificationEmail",
        { email: registrationData?.email },
      );
      navigate("/register-email-sent", { replace: true });
    } catch {
      setSendingEmail(false);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...codeDigits];
    newDigits[index] = digit;
    setCodeDigits(newDigits);
    if (digit && index < 4) {
      digitRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = codeDigits.join("");
    if (code.length < 5) return;
    setVerifyError("");
    setVerifying(true);
    try {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/verifyPhoneCode",
        { email: registrationData?.email, code },
      );
      if (resp.data?.success && resp.data?.verified) {
        setVerified(true);
      } else {
        setVerifyError(resp.data?.message ?? "Incorrect verification code.");
        setCodeDigits(["", "", "", "", ""]);
        setTimeout(() => digitRefs.current[0]?.focus(), 50);
      }
    } catch {
      setVerifyError("Something went wrong. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="registerConfirmPage">
      <div className="headline">
        <h1>Thank You For Registering and Paying</h1>
        <p className="subheadline bold">
          Confirm your account. Please choose one of the options below.
        </p>
        <p className="subheadline">
          If you've already confirmed and are only paying, please{" "}
          <Link to="/login">login again</Link> to take your exam.
        </p>
      </div>

      <div className="confirmOptions">
        <div className="confirmBox">
          <div className="boxHeader">
            <FontAwesomeIcon icon={faCommentSms} className="boxIcon" />
            <span className="boxLabel">SMS TEXT</span>
          </div>
          <p style={{ margin: 0, marginBottom: "10px", lineHeight: 1.8 }}>
            A one-time <strong>5-DIGIT</strong> code will be sent to{" "}
            <strong>{registrationData?.phone ?? "STUDENT'S PHONE"}</strong> to
            confirm the account. The code will only be valid for 5 minutes.
          </p>
          <label className="smsConsentLabel">
            <Checkbox
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              size="small"
              sx={{
                color: "#6ab03d",
                "&.Mui-checked": { color: "#6ab03d" },
                padding: "2px 6px 2px 0",
              }}
            />
            <span>
              By checking this box, I consent to receive a one-time SMS
              verification code at the phone number provided. Message and data
              rates may apply. This is a single, transactional message. Reply{" "}
              <strong>STOP</strong> to opt out. See our{" "}
              <a href="https://www.firstchoiceprep.com/privacy-policy/">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="https://www.firstchoiceprep.com/terms-of-use/">Terms</a>.
            </span>
          </label>
          <BrandButton
            onClick={async () => {
              setSendingCode(true);
              try {
                await axios.post(
                  ApiEndpoint +
                    "wp-json/firstchoiceprep/v1/sendPhoneVerification",
                  { email: registrationData?.email },
                );
              } finally {
                setVerified(false);
                setVerifyError("");
                setCodeDigits(["", "", "", "", ""]);
                setSmsModalOpen(true);
                setSendingCode(false);
              }
            }}
            disabled={sendingCode || !smsConsent}
          >
            {sendingCode ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              "SEND CODE"
            )}
          </BrandButton>
        </div>

        <div className="confirmDivider" />

        <div className="confirmBox">
          <div className="boxHeader">
            <FontAwesomeIcon icon={faAt} className="boxIcon" />
            <span className="boxLabel">EMAIL</span>
          </div>
          <p style={{ margin: 0, marginBottom: "10px", lineHeight: 1.8 }}>
            A confirmation link will be sent to{" "}
            <strong>{registrationData?.email ?? "STUDENT'S EMAIL"}</strong>.
            Please click that link to confirm the{" "}
            <strong>{registrationData?.email ?? "STUDENT'S EMAIL"}</strong>{" "}
            address.
          </p>
          {sendingEmail ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <BrandButton onClick={handleSendEmail}>SEND EMAIL</BrandButton>
          )}
        </div>
      </div>

      <Dialog
        open={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
        PaperProps={{ className: "smsModalPaper" }}
      >
        <DialogContent className="smsModalContent">
          <button
            className="smsModalClose"
            onClick={() => setSmsModalOpen(false)}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          {verified ? (
            <div className="smsModalConfirmed">
              <h2 className="smsModalTitle">YOUR ACCOUNT IS NOW CONFIRMED!</h2>
              <BrandButtonWhite
                variant="outlined"
                className="verifyBtn"
                onClick={() => navigate("/login")}
              >
                LOGIN
              </BrandButtonWhite>
            </div>
          ) : (
            <>
              <div className="smsModalHeader">
                <h2 className="smsModalTitle">ENTER CONFIRMATION CODE</h2>
                <p>
                  Please enter the 5-digit code you received on your mobile
                  device.
                </p>
              </div>
              {verifyError && (
                <p className="smsModalError">{verifyError.toUpperCase()}</p>
              )}
              <div className="codeInputs">
                {codeDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (digitRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    placeholder="#"
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="digitInput"
                  />
                ))}
              </div>
              {verifying ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  style={{ color: "#fff" }}
                />
              ) : (
                <BrandButtonWhite
                  onClick={handleVerify}
                  disabled={codeDigits.join("").length < 5}
                  variant="outlined"
                  className="verifyBtn"
                >
                  VERIFY
                </BrandButtonWhite>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegisterConfirm;
